import { useState, useCallback } from 'react';
import { useRestApi } from 'hooks/useRestApi';
import { useSnackbar } from 'hooks/useSnackbar';
import { useTranslation } from 'react-i18next';
import { apiUrl } from 'utils/apiUrl';
import { useCohortsContext } from 'contexts/CohortsContext/CohortsContext';
import { useAppConfigContext } from 'contexts/AppConfigContext';
import { SiteResponse } from './useSiteFiltering';
import { handleApiError } from '../utils';

interface CohortResponse {
  id: string;
  name: string;
  fleet_id: string;
  fleet_region: string;
  fleet_account_id: string;
  comments: string;
  status_reason: string;
  downtime_date: string;
  created_by_user: string;
  enable_notifications: boolean;
  date_created: string;
  date_updated: string;
  upgrade_workflow_execution_arn: string;
  upgrade_workflow_status: string;
  upgrade_workflow_start_date: string;
  workflow_started_by_user: string;
}

interface SiteResponseWithRegion extends SiteResponse {
  region?: string;
}

interface PaginatedResponse<T> {
  items: T[];
  next_token: string | null;
}

interface ExportProps {
  availableRegions: Array<{ value: string; label: string }>;
}

const escapeCsvField = (field: any): string => {
  if (field === null || field === undefined) return '';
  const stringValue = String(field)
    .replace(/[\r\n]+/g, ' ')
    .trim();
  if (stringValue.includes(';') || stringValue.includes('"')) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
};

const generateCSVContent = (
  sites: SiteResponseWithRegion[],
  cohortsMap: Map<string, CohortResponse>,
) => {
  const siteColumns = [
    'region',
    'captain_id',
    'client_name',
    'cohort',
    'learn_url',
    'aws_account',
    'account_name',
    'fleet_name',
    'timezone',
    'db_version',
    'db_size',
    'db_rds_type',
    'environment',
    'identifying_tag',
    'shared_rds',
    'site_contact_owner',
    'tenancy_client_id',
    'tenant_id',
    'dda',
    'performance_site',
    'asg_min_size',
    'asg_desired_capacity',
    'asg_max_size',
    'ec2_instance_types',
    'instances_os',
    'learn_status',
    'is_candidate',
    'domain_certificate_arn',
    'date_created',
    'date_updated',
    'upgrade_job_id',
    'upgrade_job_start_date',
    'upgrade_job_end_date',
    'upgrade_job_status',
    'upgrade_job_status_reason',
    'site_status',
  ];

  const cohortColumns = [
    'id',
    'name',
    'fleet_id',
    'fleet_region',
    'fleet_account_id',
    'status_reason',
    'downtime_date',
    'enable_notifications',
    'date_created',
    'date_updated',
    'upgrade_workflow_execution_arn',
    'upgrade_workflow_status',
    'upgrade_workflow_start_date',
    'workflow_started_by_user',
  ];

  const headers = [...siteColumns, ...cohortColumns.map((c) => `cohort_${c}`)];

  let csvContent = `${headers.join(';')}\r\n`;

  sites.forEach((site) => {
    const row: string[] = [];
    const cohortId = site.cohort;
    const cohortData =
      cohortId && cohortId !== 'NO_COHORT_ASSIGNED' ? cohortsMap.get(cohortId) : null;

    siteColumns.forEach((col) => {
      // Accessing dynamic property safely casted to any to avoid TS error without ignore comments
      row.push(escapeCsvField((site as any)[col]));
    });

    cohortColumns.forEach((col) => {
      if (cohortData) {
        row.push(escapeCsvField(cohortData[col as keyof CohortResponse]));
      } else {
        row.push('');
      }
    });

    csvContent += `${row.join(';')}\r\n`;
  });

  return csvContent;
};

const toGmtReportString = (value?: string): string => {
  if (!value) return '';
  const hasZone = /Z|[+-]\d{2}:?\d{2}$/.test(value);
  const date = new Date(hasZone ? value : `${value}Z`);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 16).replace('T', ' ');
};

const formatSuggestedDateTime = (site: SiteResponseWithRegion): string => {
  const start = site?.notifications?.start_suggested_date;
  const end = site?.notifications?.end_suggested_date;
  if (!start) return '';
  const startStr = toGmtReportString(start);
  if (end && end !== start) {
    return `${startStr} - ${toGmtReportString(end)}`;
  }
  return startStr;
};

const generateDeclinedCSVContent = (
  sites: SiteResponseWithRegion[],
  cohortsMap: Map<string, CohortResponse>,
): string => {
  const headers = [
    'client_name',
    'url',
    'fleet_environment',
    'captain_id',
    'cohort_name',
    'cohort_created_by',
    'current_downtime_date_gmt (YYYY-MM-DD HH:mm)',
    'new_suggested_date_time_gmt (YYYY-MM-DD HH:mm)',
  ];

  let csvContent = `${headers.join(';')}\r\n`;

  sites.forEach((site) => {
    const cohortData =
      site.cohort && site.cohort !== 'NO_COHORT_ASSIGNED' ? cohortsMap.get(site.cohort) : undefined;
    const cohortName = site.cohort_name || cohortData?.name || '';

    const row = [
      escapeCsvField(site.client_name),
      escapeCsvField(site.learn_url),
      escapeCsvField(site.fleet_name),
      escapeCsvField(site.captain_id),
      escapeCsvField(cohortName),
      escapeCsvField(cohortData?.created_by_user),
      escapeCsvField(toGmtReportString(cohortData?.downtime_date)),
      escapeCsvField(formatSuggestedDateTime(site)),
    ];

    csvContent += `${row.join(';')}\r\n`;
  });

  return csvContent;
};

const triggerFileDownload = (content: string, fileName: string) => {
  const BOM = '\uFEFF';
  const blob = new Blob([BOM + content], { type: 'text/csv;charset=utf-8;' });

  const link = document.createElement('a');
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

export const useSitesReportGenerator = ({ availableRegions }: ExportProps) => {
  const { selectedRegion } = useCohortsContext();
  const { stage } = useAppConfigContext();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusLabel, setStatusLabel] = useState('');

  const sitesApi = useRestApi<PaginatedResponse<SiteResponse>>('', { manual: true });
  const cohortsApi = useRestApi<PaginatedResponse<CohortResponse>>('', { manual: true });

  const fetchAllPages = useCallback(async <T>(apiClient: any, endpoint: string): Promise<T[]> => {
    let allItems: T[] = [];
    let nextToken: string | null = null;
    let hasMore = true;

    while (hasMore) {
      const params: Record<string, string> = { results_count: '400' };
      if (nextToken) params.next_token = nextToken;

      // eslint-disable-next-line no-await-in-loop
      const response = await apiClient.fetch({
        url: endpoint,
        method: 'GET',
        params,
      });

      if (response && response.data && response.data.items) {
        allItems = [...allItems, ...response.data.items];
        nextToken = response.data.next_token;
        hasMore = !!nextToken;
      } else {
        hasMore = false;
      }
    }
    return allItems;
  }, []);

  const fetchDataForRegion = useCallback(
    async (region: string) => {
      try {
        const [sites, cohorts] = await Promise.all([
          fetchAllPages<SiteResponse>(sitesApi, apiUrl('lct', `regions/${region}/search`)),
          fetchAllPages<CohortResponse>(cohortsApi, apiUrl('lct', `regions/${region}/cohorts`)),
        ]);
        return { sites, cohorts };
      } catch (error) {
        handleApiError(error, { operation: 'generateReport', enqueueSnackbar, t });
        return { sites: [], cohorts: [] };
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sitesApi, cohortsApi, fetchAllPages],
  );

  const generateCurrentRegionReport = useCallback(async () => {
    setIsGenerating(true);
    setProgress(0);
    setStatusLabel(`Fetching data for ${selectedRegion}...`);

    try {
      setProgress(10);
      const { sites, cohorts } = await fetchDataForRegion(selectedRegion);
      setProgress(80);

      if (sites.length === 0) {
        setIsGenerating(false);
        return;
      }

      const sitesWithRegion: SiteResponseWithRegion[] = sites.map((site) => ({
        ...site,
        region: selectedRegion,
      }));

      const cohortsMap = new Map<string, CohortResponse>();
      cohorts.forEach((c) => cohortsMap.set(c.id, c));

      setStatusLabel('Generating CSV file...');
      const csv = generateCSVContent(sitesWithRegion, cohortsMap);

      const timestamp = new Date().toISOString().split('T')[0];
      triggerFileDownload(csv, `sites_report_${selectedRegion}_${timestamp}.csv`);
      setProgress(100);
    } catch (error) {
      handleApiError(error, { operation: 'generateReport', enqueueSnackbar, t });
    } finally {
      setTimeout(() => setIsGenerating(false), 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRegion, fetchDataForRegion]);

  const generateGlobalReport = useCallback(async () => {
    setIsGenerating(true);
    setProgress(0);
    setStatusLabel('Initializing global export...');

    try {
      const regionsToFetch =
        stage === 'prod'
          ? availableRegions
          : availableRegions.filter((r) => r.value === selectedRegion);

      let allGlobalSites: SiteResponseWithRegion[] = [];
      const globalCohortsMap = new Map<string, CohortResponse>();
      const totalRegions = regionsToFetch.length;

      for (let i = 0; i < totalRegions; i += 1) {
        const regionOption = regionsToFetch[i];
        setStatusLabel(`Fetching data for ${regionOption.label} (${i + 1}/${totalRegions})...`);

        // eslint-disable-next-line no-await-in-loop
        const { sites, cohorts } = await fetchDataForRegion(regionOption.value);

        const sitesWithRegion = sites.map((site) => ({
          ...site,
          region: regionOption.value,
        }));

        allGlobalSites = [...allGlobalSites, ...sitesWithRegion];
        cohorts.forEach((c) => globalCohortsMap.set(c.id, c));

        const percentage = Math.round(((i + 1) / totalRegions) * 100);
        setProgress(percentage);
      }

      if (allGlobalSites.length === 0) {
        setIsGenerating(false);
        return;
      }

      setStatusLabel('Finalizing and downloading report...');
      const csv = generateCSVContent(allGlobalSites, globalCohortsMap);
      const timestamp = new Date().toISOString().split('T')[0];
      triggerFileDownload(csv, `sites_report_GLOBAL_${timestamp}.csv`);
    } catch (error) {
      handleApiError(error, { operation: 'generateReport', enqueueSnackbar, t });
    } finally {
      setTimeout(() => setIsGenerating(false), 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableRegions, fetchDataForRegion, stage, selectedRegion]);

  const generateDeclinedUpgradesReport = useCallback(async () => {
    setIsGenerating(true);
    setProgress(0);
    setStatusLabel(`Fetching declined upgrades for ${selectedRegion}...`);

    try {
      const { sites, cohorts } = await fetchDataForRegion(selectedRegion);

      const declinedSites: SiteResponseWithRegion[] = sites
        .filter((site) => site.site_status === 'DECLINED')
        .map((site) => ({ ...site, region: selectedRegion }));

      if (declinedSites.length === 0) {
        enqueueSnackbar(
          t(
            'cohorts.reportGenerationOptions.noDeclined',
            'No declined upgrades found in the selected region.',
          ),
          { variant: 'info' },
        );
        setIsGenerating(false);
        return;
      }

      setProgress(80);
      const cohortsMap = new Map<string, CohortResponse>();
      cohorts.forEach((c) => cohortsMap.set(c.id, c));

      setStatusLabel('Generating CSV file...');
      const csv = generateDeclinedCSVContent(declinedSites, cohortsMap);

      const timestamp = new Date().toISOString().split('T')[0];
      triggerFileDownload(csv, `declined_upgrades_report_${selectedRegion}_${timestamp}.csv`);
      setProgress(100);
    } catch (error) {
      handleApiError(error, { operation: 'generateReport', enqueueSnackbar, t });
    } finally {
      setTimeout(() => setIsGenerating(false), 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRegion, fetchDataForRegion]);

  return {
    generateCurrentRegionReport,
    generateGlobalReport,
    generateDeclinedUpgradesReport,
    isGenerating,
    progress,
    statusLabel,
  };
};
